import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  CheckCircle, AlertCircle, Loader2, Building2, User, 
  Gift, CreditCard, Clock, Shield, ArrowRight, LogIn,
  ExternalLink, UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClaimProfile() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    fetchTransferDetails();
  }, [token]);

  const fetchTransferDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile-transfer/${token}`);
      setTransfer(response.data);
    } catch (err) {
      console.error('Failed to fetch transfer details:', err);
      if (err.response?.status === 404) {
        setError('This transfer link is invalid or has expired.');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'This transfer has already been processed.');
      } else {
        setError('Failed to load transfer details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/claim-profile/${token}`);
      return;
    }

    setClaiming(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/profile-transfer/${token}/claim`,
        {},
        { withCredentials: true }
      );
      
      setClaimed(true);
      setClaimResult(response.data);
      refreshUser();
    } catch (err) {
      console.error('Failed to claim profile:', err);
      if (err.response?.status === 403) {
        setError('This transfer was sent to a different email address. Please login with the correct account.');
      } else {
        setError(err.response?.data?.detail || 'Failed to claim profile. Please try again.');
      }
    } finally {
      setClaiming(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="claim-profile-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#C41E3A] mx-auto mb-4" />
          <p className="text-zinc-400">Loading transfer details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="claim-profile-error">
        <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Transfer Unavailable</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (claimed && claimResult) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="claim-profile-success">
        <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Profile Claimed Successfully!</h1>
          <p className="text-zinc-400 mb-6">
            You are now the owner of <span className="text-white font-medium">{transfer.profile_name}</span>. 
            You can edit and manage your profile from the dashboard.
          </p>
          
          {claimResult.subscription_handling === 'coupon' && claimResult.coupon_code && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
              <Gift className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-purple-400 text-sm mb-2">A coupon code has been applied to your profile:</p>
              <p className="text-white font-mono text-lg">{claimResult.coupon_code}</p>
            </div>
          )}
          
          {claimResult.subscription_handling === 'pay' && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
              <CreditCard className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-amber-400 text-sm">
                Your profile is inactive. Please subscribe to make it publicly visible.
              </p>
            </div>
          )}
          
          <button
            onClick={() => navigate(claimResult.redirect_url)}
            className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="go-to-profile-btn"
          >
            Edit Your Profile
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="claim-profile-page">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" 
              alt="BrandStage" 
              className="h-12 w-auto mx-auto"
            />
          </Link>
          <h1 className="text-3xl font-bold mb-2">Claim Your Profile</h1>
          <p className="text-zinc-400">Your BrandStage profile is ready for you to take ownership</p>
        </div>
        
        {/* Profile Preview Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#C41E3A]/20 to-purple-500/20 p-6 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-zinc-800 flex items-center justify-center">
                  {transfer.profile_type === 'company' ? (
                    <Building2 className="w-8 h-8 text-zinc-400" />
                  ) : (
                    <User className="w-8 h-8 text-zinc-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold" data-testid="profile-name">{transfer.profile_name}</h2>
                  <p className="text-zinc-400 text-sm capitalize">
                    {transfer.profile_type === 'company' ? 'Company Profile' : 'Leadership Profile'}
                  </p>
                </div>
              </div>
              <a
                href={transfer.profile_slug ? `/p/${transfer.profile_slug}` : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1 text-sm transition-colors ${
                  transfer.profile_slug 
                    ? 'text-[#C41E3A] hover:text-white cursor-pointer' 
                    : 'text-zinc-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (!transfer.profile_slug) {
                    e.preventDefault();
                  }
                }}
                data-testid="preview-profile-link"
              >
                <ExternalLink className="w-4 h-4" />
                Preview Profile
              </a>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {/* Subscription Status */}
            <div className="flex items-start gap-3 p-4 bg-zinc-800/50 rounded-xl">
              {transfer.subscription_handling === 'keep' && (
                <>
                  <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-400">Active Subscription Included</p>
                    <p className="text-sm text-zinc-400">
                      Your profile subscription is active until{' '}
                      <span className="text-white">
                        {transfer.current_active_until 
                          ? new Date(transfer.current_active_until).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </span>
                    </p>
                  </div>
                </>
              )}
              
              {transfer.subscription_handling === 'coupon' && (
                <>
                  <Gift className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-purple-400">Coupon Code Included</p>
                    <p className="text-sm text-zinc-400">
                      A promotional coupon <span className="text-white font-mono">{transfer.coupon_code}</span> is ready for your subscription.
                    </p>
                  </div>
                </>
              )}
              
              {transfer.subscription_handling === 'pay' && (
                <>
                  <CreditCard className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-400">Payment Required</p>
                    <p className="text-sm text-zinc-400">
                      After claiming, you'll need to subscribe ({transfer.currency_mode === 'usd' ? 'USD' : transfer.currency_mode === 'inr' ? 'INR' : 'INR/USD'}) to make your profile publicly visible.
                    </p>
                  </div>
                </>
              )}
            </div>
            
            {/* What You Get */}
            <div className="space-y-2">
              <p className="text-sm text-zinc-500 uppercase tracking-wider">Once claimed, you can:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Edit your profile content and videos
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  View analytics and engagement metrics
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Manage your subscription
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Post opportunities on BrandStage Connect <span className="text-amber-400 text-xs">(Coming Soon)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Action Section */}
        {user ? (
          <div className="space-y-4">
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Logged in as</p>
                <p className="text-sm text-zinc-400">{user.email}</p>
              </div>
            </div>
            
            <button
              onClick={handleClaim}
              disabled={claiming}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-4 rounded-xl font-bold text-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="claim-btn"
            >
              {claiming ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Claiming Profile...
                </>
              ) : (
                <>
                  Claim This Profile
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-center text-zinc-400 text-sm">
              Please login or create an account to claim this profile
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Link
                to={`/login?redirect=/claim-profile/${token}`}
                className="flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-xl font-medium transition-colors"
                data-testid="login-to-claim-btn"
              >
                <LogIn className="w-4 h-4" />
                Login
              </Link>
              <Link
                to={`/register?redirect=/claim-profile/${token}`}
                className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-xl font-medium transition-colors border border-zinc-700"
                data-testid="register-to-claim-btn"
              >
                <UserPlus className="w-4 h-4" />
                Create Account
              </Link>
            </div>
          </div>
        )}
        
        {/* Footer Note */}
        <p className="text-center text-zinc-500 text-xs mt-6">
          <Clock className="w-3 h-3 inline mr-1" />
          This link expires in 7 days from the date it was sent
        </p>
      </div>
    </div>
  );
}
